import React, {useMemo} from 'react'
import {StripeElementStyle} from '@stripe/stripe-js'
import BackButtonButton from './BackButton'
import PurchaseButton from './PurchaseButton'
import {formatPrice} from '../../lib/currency'
import {usePurchaseForm} from './PurchaseFormProvider'
import {usePurchase} from '../../PurchaseContext'
import {PurchaseFormBlock} from '.'
import Heading from './Heading'
import CardFields from '../../lib/ui/stripe/CardFields'
import styles from './PurchaseForm.module.css'
import {useCountryName, useStateName} from '../../lib/CountriesProvider'
import {BlockComponentProps} from '../index'

interface OrderConfirmationStepProps
  extends PurchaseFormBlock,
    BlockComponentProps {
  onBack: () => void
  ButtonComponent: React.FC<any>
}

export default function OrderConfirmationStep(
  props: OrderConfirmationStepProps,
) {
  const {onBack, ButtonComponent, ...config} = props

  const {
    input: {fontSize: inputFontSize},
    summary,
  } = config

  const {selectedTicket} = usePurchase()

  const {useSameAddressForShipping, setCardElement, ...form} = usePurchaseForm()

  const billingCountry = useCountryName(form.billingCountryId)
  const billingState = useStateName(form.billingCountryId, form.billingStateId)

  const shippingCountry = useCountryName(form.shippingCountryId)
  const shippingState = useStateName(
    form.shippingCountryId,
    form.shippingStateId,
  )

  const requiresCardInput = selectedTicket ? selectedTicket.price !== 0 : false

  const billing = {
    address_1: form.billingAddress1,
    address_2: form.billingAddress2,
    city: form.billingCity,
    state_id: form.billingStateId,
    zip_postal: form.billingZipPostal,
    country_id: form.billingCountryId,
  }

  const shipping = useSameAddressForShipping
    ? billing
    : {
        address_1: form.shippingAddress1,
        address_2: form.shippingAddress2,
        city: form.shippingCity,
        state_id: form.shippingStateId,
        zip_postal: form.shippingZipPostal,
        country_id: form.shippingCountryId,
      }

  const style: StripeElementStyle = useMemo(
    () => ({
      base: {
        fontFamily: 'Rubik, sans-serif',
        fontSize: `${inputFontSize}px`,
        ':disabled': {
          color: 'rgba(0, 0, 0, 0.36)',
        },
        '::placeholder': {
          color: 'rgba(0, 0, 0, 0.36)',
        },
      },
      invalid: {
        color: '#f44336',
      },
    }),
    [inputFontSize],
  )

  return (
    <>
      <style>
        {`
      .StripeElement {
        background-color: ${config.input.backgroundColor} !important;
        border: ${config.input.border.width}px solid !important;
        border-color: ${config.input.border.color} !important;
        border-radius: ${config.input.border.radius}px !important;
        padding-left: ${config.input.padding.left}px !important;
        padding-right: ${config.input.padding.right}px !important;
        padding-top: ${config.input.padding.top * 1.5}px !important;
        padding-bottom: ${config.input.padding.bottom * 1.5}px !important;
      }
        `}
      </style>
      <div
        style={{
          fontSize: summary.text.fontSize + 'px',
          color: summary.text.color,
        }}
      >
        <div>
          <Heading
            style={{
              fontWeight: 600,
              fontSize: summary.label.fontSize + 'px',
              color: summary.label.color,
            }}
          >
            {summary.label.attendeeDetailsLabel}
          </Heading>
          <div>
            {form.firstName} {form.lastName}
          </div>
          <div>{form.email}</div>
          <div>{form.phoneNumber}</div>
        </div>
        {config.requireAddresses && (
          <div>
            <div>
              <div>
                <div>
                  <Heading
                    style={{
                      fontWeight: 600,
                      fontSize: summary.label.fontSize + 'px',
                      color: summary.label.color,
                    }}
                  >
                    {summary.label.billingAddressLabel}
                  </Heading>
                  <div>{billing.address_1}</div>
                  <div>{billing.address_2}</div>
                  <div>
                    {billing.city}, {billingState}, {billing.zip_postal}
                  </div>
                  <div>{billingCountry}</div>
                </div>
              </div>
              <div>
                <div>
                  <Heading
                    style={{
                      fontWeight: 600,
                      fontSize: summary.label.fontSize + 'px',
                      color: summary.label.color,
                    }}
                  >
                    {summary.label.shippingAddressLabel}
                  </Heading>
                  <div>{shipping.address_1}</div>
                  <div>{shipping.address_2}</div>
                  <div>
                    {shipping.city}, {shippingState}, {shipping.zip_postal}
                  </div>
                  <div>{shippingCountry}</div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div>
          <div
            style={{
              marginBottom: '8px',
            }}
          >
            <div>
              <Heading
                style={{
                  fontWeight: 600,
                  fontSize: summary.label.fontSize + 'px',
                  color: summary.label.color,
                }}
              >
                {summary.label.purchaseLabel}
              </Heading>
            </div>
            <div>{selectedTicket?.name}</div>
          </div>
          <div>
            <div>
              <Heading
                style={{
                  fontWeight: 600,
                  fontSize: summary.label.fontSize + 'px',
                  color: summary.label.color,
                }}
              >
                {summary.label.totalLabel}
              </Heading>
            </div>
            <div>
              ${selectedTicket ? formatPrice(selectedTicket.price) : '-'}
            </div>
          </div>
        </div>
        {requiresCardInput && (
          <div>
            <CardFields style={style} onLoad={setCardElement} />
          </div>
        )}
        <div className={styles.formButtonBox}>
          <BackButtonButton {...config} onClick={onBack} />
          <PurchaseButton ButtonComponent={ButtonComponent} {...config} />
        </div>
      </div>
    </>
  )
}
